import { NavigationState, PartialState } from '@react-navigation/native';

import { signedInRoutePaths, signedOutRoutePaths } from './routeDefinitions';
import type {
  AllSignedInRoutes,
  CustomerDetailStackParamList,
  SignedOutRootStackParamList,
} from './types';

// NOTE: internally got this from getPathFromState(@react-natigation/native) since it's not exported
type State = NavigationState | Omit<PartialState<NavigationState>, 'stale'>;

// Re-export parse functions from parseFunctions for backward compatibility
export {
  clearLinkingError,
  emitInvalidParamsError,
  LINKING_INVALID_PARAMS_MESSAGE,
  parseNumberParam,
  parseOptionalJsonParam,
  parseOptionalNumberParam,
  parseOptionalStringParam,
  parseStringParam,
  subscribeLinkingErrors,
} from './parseFunctions';

/**
 * List of modal screen names that should NOT appear in the URL.
 * These are overlay screens that don't need deep link support.
 */
const modalRoutesToExcludeFromUrl = new Set([
  'OrderHistoryModal',
  'ManagementAnalysisDetails',
  'ImpersonateMenu',
]);

export const serializeNavigationParamValue = (
  value: unknown
): string | number | boolean | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    const serializedArray = value
      .map((item) => serializeNavigationParamValue(item))
      .filter((item): item is string | number | boolean => item !== undefined);

    if (!serializedArray.length) {
      return undefined;
    }

    try {
      return JSON.stringify(serializedArray);
    } catch (_error) {
      return undefined;
    }
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_error) {
      return undefined;
    }
  }

  return undefined;
};

export const sanitizeNavigationState = (state: State): State => {
  if (!state) {
    return state;
  }

  // Filter out modal routes from the navigation state before URL serialization
  const filteredRoutes = state.routes?.filter(
    (route: any) => !modalRoutesToExcludeFromUrl.has(route.name)
  );

  // Calculate the new index: if the current focused route was filtered out,
  // walk backwards to find the previous non-excluded route; otherwise, find the new index of the focused route
  let newIndex = -1;
  if (filteredRoutes && state.routes && state.index !== undefined) {
    const currentRouteName = state.routes[state.index]?.name;
    if (modalRoutesToExcludeFromUrl.has(currentRouteName)) {
      // Current route was excluded, walk backwards to find the first non-excluded route
      for (let i = state.index - 1; i >= 0; i--) {
        const route = state.routes[i];
        if (!modalRoutesToExcludeFromUrl.has(route.name)) {
          newIndex = filteredRoutes.findIndex((r: any) => r.key === route.key);
          if (newIndex !== -1) {
            break;
          }
        }
      }
      // If no valid previous route found, fall back to first route
      if (newIndex === -1) {
        newIndex = 0;
      }
    } else if (state.index !== undefined) {
      // Find the new index of the current route in the filtered array
      // @ts-expect-error - state.index isn't undefined here, but compiler don't seem to understand that
      newIndex = filteredRoutes.findIndex((r: any) => r.key === state.routes[state.index]?.key);
      if (newIndex === -1) {
        newIndex = 0;
      }
    } else {
      newIndex = 0;
    }
  }

  const nextState = {
    ...state,
    index: newIndex >= 0 ? newIndex : 0,
    routes: filteredRoutes?.map((route: any) => {
      const { state: childState, params, ...restRoute } = route;

      const sanitizedParams = params
        ? Object.fromEntries(
            Object.entries(params)
              .map(([key, value]) => [key, serializeNavigationParamValue(value)])
              .filter(([, value]) => value !== undefined)
          )
        : undefined;

      return {
        ...restRoute,
        ...(sanitizedParams ? { params: sanitizedParams } : {}),
        ...(childState ? { state: sanitizeNavigationState(childState) } : {}),
      };
    }),
  };

  return nextState;
};

// ============================================================================
// Type Definitions for Href Builders
// ============================================================================

export type SignedInLinkableRoute = keyof typeof signedInRoutePaths;
export type SignedOutLinkableRoute = keyof typeof signedOutRoutePaths;

/**
 * Maps flattened route names to their param types from the original ParamList.
 * CustomerDetail and PrivatePensionApplication are flattened from CustomerDetailGroup.
 */
type SignedInRouteParams = {
  [K in keyof AllSignedInRoutes]: AllSignedInRoutes[K];
} & {
  CustomerDetail: CustomerDetailStackParamList['CustomerDetail'];
  PrivatePensionApplication: CustomerDetailStackParamList['PrivatePensionApplication'];
  ManagementAnalysisDetails: AllSignedInRoutes['ManagementAnalysisDetails'] & {
    produtoId?: string;
  };
  SubscriptionDetails: AllSignedInRoutes['SubscriptionDetails'] & { subscriptionId?: string };
  EquityOrderEdit: AllSignedInRoutes['EquityOrderEdit'] & { orderId?: string };
  RedeemBatchTicketDetails: AllSignedInRoutes['RedeemBatchTicketDetails'];
  BatchTicketSent: AllSignedInRoutes['BatchTicketSent'] & { basketAdvisorOrderId?: string };
};

type SignedOutRouteParams = SignedOutRootStackParamList;

// ============================================================================
// Href Builder Functions
// ============================================================================

/**
 * Extracts path parameter names from a path template.
 * e.g., 'meus-clientes/:customerId/contas/:accountId' => ['customerId', 'accountId']
 */
const extractPathParams = (pathTemplate: string): string[] => {
  const matches = pathTemplate.matchAll(/:([a-zA-Z0-9_]+)\??/g);
  return Array.from(matches, (match) => match[1]);
};

/**
 * Builds a URL path by replacing path parameters and appending query parameters.
 *
 * @param pathTemplate - The path template with :param placeholders
 * @param params - The parameters object
 * @param basePrefix - Optional prefix to prepend to the path
 * @returns The constructed URL path
 */
const buildHref = (
  pathTemplate: string,
  params: Record<string, unknown> | undefined,
  basePrefix: string
): string => {
  const pathParams = extractPathParams(pathTemplate);
  const pathSegments = pathTemplate.split('/').filter((segment) => segment.length > 0);

  const path = pathSegments
    .map((segment) => {
      if (!segment.startsWith(':')) {
        return segment;
      }

      const isOptional = segment.endsWith('?');
      const paramName = segment.slice(1, isOptional ? -1 : undefined);
      const value = params?.[paramName];
      const serialized = serializeNavigationParamValue(value);

      if (serialized !== undefined) {
        return encodeURIComponent(String(serialized));
      }

      // Optional params are removed from the path when not provided.
      if (isOptional) {
        return null;
      }

      // Keep empty segment for missing required params to preserve invalid path behavior.
      return '';
    })
    .filter((segment): segment is string => segment !== null)
    .join('/');

  // Collect remaining params as query string
  const queryParams: string[] = [];

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      // Skip path params and undefined values
      if (pathParams.includes(key)) {
        continue;
      }

      const serialized = serializeNavigationParamValue(value);

      if (serialized !== undefined) {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(serialized))}`);
      }
    }
  }

  const fullPath = `${basePrefix}${path}`;
  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

  return `${fullPath}${queryString}`;
};

/**
 * Builds a URL href for a signed-in route.
 * All signed-in routes are prefixed with `[prefix_if_present]/`.
 *
 * @example
 * // Simple route without params
 * buildSignedInHref('Cockpit') // => '[prefix_if_present]/cockpit'
 *
 * @example
 * // Route with path params
 * buildSignedInHref('CustomerDetail', { customerId: 123, accountId: 456 })
 * // => '[prefix_if_present]/meus-clientes/123/contas/456'
 *
 * @example
 * // Route with path params and query params
 * buildSignedInHref('CustomerDetail', { customerId: 123, accountId: 456, selectedTab: 'application' })
 * // => '[prefix_if_present]/meus-clientes/123/456/detalhe/application'
 *
 * @example
 * // Route with complex object param (serialized to JSON)
 * buildSignedInHref('Resume', { type: 'Fund', fullProductItem: { id: 1, name: 'Product' } })
 * // => '[prefix_if_present]/cockpit/resumo?type=Fund&fullProductItem=%7B%22id%22%3A1%2C%22name%22%3A%22Product%22%7D'
 */
export const buildSignedInHref = <T extends SignedInLinkableRoute>(
  route: T,
  params?: T extends keyof SignedInRouteParams ? SignedInRouteParams[T] : Record<string, unknown>
): string => {
  const pathTemplate = signedInRoutePaths[route];
  return buildHref(pathTemplate, params as Record<string, unknown>, '/');
};

/**
 * Builds a URL href for a signed-out route.
 *
 * @example
 * // Simple route without params
 * buildSignedOutHref('SignIn') // => '/'
 *
 * @example
 * // Route with path params
 * buildSignedOutHref('UnlockConfirmation', { confirmationToken: 'abc123' })
 * // => '/confirmacao/abc123'
 *
 * @example
 * // Route with query params
 * buildSignedOutHref('SignIn', { initialPage: 'home', cbid: 'xyz' })
 * // => '/?initialPage=home&cbid=xyz'
 */
export const buildSignedOutHref = <T extends SignedOutLinkableRoute>(
  route: T,
  params?: T extends keyof SignedOutRouteParams ? SignedOutRouteParams[T] : Record<string, unknown>
): string => {
  const pathTemplate = signedOutRoutePaths[route];
  return buildHref(pathTemplate, params as Record<string, unknown>, '/');
};
